import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQuery as useApolloQuery, useLazyQuery } from '@apollo/client';
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  Download,
  Filter,
  Calendar,
  MapPin,
  Play,
  Clock,
  BarChart3,
  Eye,
  FileText,
  FileSpreadsheet,
  ArrowLeft,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DELIVERY_REPORTS_QUERY } from '../Graphql/Queries';
import { ASSIGN_CREATIVES_PACKAGE_INVENTORIES } from '../Graphql/packageQurie';
import { generateMockData } from '../Graphql/MockData';
import client from "@/Graphql/apolloClient";


// GraphQL interfaces matching the API response
interface Agency {
  id: string;
  name: string;
}

interface StartEndDate {
  date: string;
  dateStr?: string;
  dateFmt?: string;
}

interface InventoryDetails {
  id: string;
  type: string;
  billboardId: string;
  referenceId?: string;
  deviceId?: string;
  name?: string;
  displayName?: string;
  company?: string;
  facingDirection?: string;
  category?: string;
  group?: string;
  format?: string;
  venueType?: string;
  latitude?: number;
  longitude?: number;
  active?: boolean;
  mediaOwnerId?: string;
  mediaOwnerName?: string;
}

interface Report {
  inventoryType: string;
  id: string;
  inventoryReferenceId: string;
  campaignName: string;
  campaignId: string;
  campaignStatus: string;
  dealId: string;
  dsp: string;
  agency: Agency;
  startDate: StartEndDate;
  endDate: StartEndDate;
  inventoryName: string;
  inventoryId: string;
  inventoryThumbnailUrl: string;
  inventoryPrice: number;
  availableBooking: number;
  nextAvailableDate: string;
  spotsAvailability: number;
  inventoryDetails: InventoryDetails;
  totalPages?: number;
  mediaOwnerId?: string;
  mediaOwnerName?: string;
  isSpotBased?: boolean;
}

interface DeliveryReportItem {
  id: string;
  campaignId: string;
  billboardId: string;
  billboardName: string;
  billboardImage: string;
  date: string;
  startTime?: string;
  endTime?: string;
  loopDuration?: number;
  slotDuration?: number;
  playCount?: number;
  impressions?: number;
  creativeName: string;
  creativeId: string;
  loopShare?: number;
  billboardType: string;
  startDate: string;
  endDate: string;
  contentType: string;
  mediaOwnerId?: string;
  mediaOwnerName?: string;
  status: 'active' | 'completed' | 'pending';
  isSpotBased?: boolean;
}

interface CampaignInfo {
  id: string;
  name: string;
  dealId: string;
  duration: string;
  startDate: string;
  endDate: string;
  status: string;
}

interface DeliveryReportProps {
  base_path?: string;
}

const _inventoryTypeDisplayMapping: { [key: string]: string } = {
  'digital': 'Digital',
  'digital_billboard': 'Digital',
  'classic': 'Classic',
  'static': 'Classic',
  'static_billboard': 'Classic',
  'deal': 'Deal'
};

// Helper function to get purchase type display value
const getPurchaseType = (billboardType: string, contentType: string, isSpotBased?: boolean): string => {
  if ((contentType === 'digital' || billboardType === 'digital') && isSpotBased) {
    return 'Spot-based';
  } else {
    return 'Day-based';
  }
};

export default function DeliveryReport({ base_path = '' }: DeliveryReportProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign') || '1';

  const [campaign, setCampaign] = useState<CampaignInfo | null>(null);
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryReportItem[]>([]);
  const [deliveryreports, setDeliveryReports] = useState<any[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<DeliveryReportItem[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState("all");
  const [selectedBillboard, setSelectedBillboard] = useState("all");
  const [startDate, setStartDate] = useState("2023-07-01");
  const [endDate, setEndDate] = useState("2023-07-31");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemsPerPage] = useState(10);
  const [expandedPackages, setExpandedPackages] = useState<string[]>([]);
  const [packageInventories, setPackageInventories] = useState<{ [key: string]: any[] }>({});
  const [currentlyFetchingPackage, setCurrentlyFetchingPackage] = useState<string | null>(null);

  // Authentication and environment variables (matching other pages approach)
  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  const companyId = localStorage.getItem('companyId');

  console.log('Delivery Report Debug:', {
    campaignId,
    token: token ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  const [getPackageInventories, { data: packageInventoriesData, loading: packageInventoriesLoading }] = useLazyQuery(
    ASSIGN_CREATIVES_PACKAGE_INVENTORIES,
    {
      client,
      onError: (err) => {
        console.error('Fetch Package Inventories Error:', err);
      }
    }
  );

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Fetch fresh user data with network-only policy to bypass cache
      const { data: fetchedUserData } = await client.query({
        query: DELIVERY_REPORTS_QUERY,
        variables: {
          accessToken: token,
          campaignId: campaignId,
          page: currentPage - 1,
          size: itemsPerPage,
          sort: "lastModifiedDate,desc",
          search: searchTerm,
        },
        fetchPolicy: 'network-only', // Bypass cache to get fresh data
      });
      setDeliveryReports(fetchedUserData.deliveryReports);
      console.log("Fetched Delivery Reports:", fetchedUserData);

      // setDeliveryLogs(fetchedUserData.deliveryReports || []);
    } catch (error) {
      console.error("Error fetching delivery reports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle package expansion
  const togglePackageExpansion = (billboardId: string) => {
    const isExpanding = !expandedPackages.includes(billboardId);

    setExpandedPackages(prev =>
      prev.includes(billboardId)
        ? prev.filter(id => id !== billboardId)
        : [...prev, billboardId]
    );

    // Fetch package inventories when expanding if not already fetched
    if (isExpanding && !packageInventories[billboardId]) {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Generate mock package inventories for testing
        // Simulate some packages having inventories and others being empty
        const shouldHaveInventories = Math.random() > 0.3; // 70% chance of having inventories

        const mockPackageInventories = shouldHaveInventories ? [
          {
            inventoryId: `${billboardId}_inv_1`,
            name: `Billboard A - ${billboardId}`,
            type: 'Digital',
            mediaOwnerName: 'City Media Co.',
            imageUrl: "https://picsum.photos/400/300?random=1"
          },
          {
            inventoryId: `${billboardId}_inv_2`,
            name: `Billboard B - ${billboardId}`,
            type: 'Digital',
            mediaOwnerName: 'Urban Display Ltd.',
            imageUrl: "https://picsum.photos/400/300?random=2"
          },
          {
            inventoryId: `${billboardId}_inv_3`,
            name: `Billboard C - ${billboardId}`,
            type: 'Digital',
            mediaOwnerName: 'Metro Screens Inc.',
            imageUrl: "https://picsum.photos/400/300?random=3"
          }
        ] : [];

        setPackageInventories(prev => ({
          ...prev,
          [billboardId]: mockPackageInventories
        }));
      } else {
        const billboard = deliveryLogs.find(b => b.id === billboardId);

        if (billboard && billboard.billboardId) {
          setCurrentlyFetchingPackage(billboardId);
          getPackageInventories({
            variables: {
              accessToken: token || '',
              packageId: billboard.billboardId,
              page: 0,
              size: 999
            }
          });
        }
      }
    } else if (isExpanding) {
      console.log('Package inventories already cached for billboard:', billboardId);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
          const mockResponse = await generateMockData(DELIVERY_REPORTS_QUERY);
          console.log("Mock Response:", mockResponse);
          setDeliveryReports(mockResponse.data.deliveryReports || []);
        } else {
          await fetchData();
        }
      } catch (error) {
        console.error("Error initializing delivery reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [token, campaignId, currentPage, itemsPerPage, searchTerm]);

  // Update package inventories
  useEffect(() => {
    if (packageInventoriesData?.assaigncreativesPackageInventories && currentlyFetchingPackage) {
      const inventories = packageInventoriesData.assaigncreativesPackageInventories;

      // Store the inventories for the specific package we were fetching
      setPackageInventories(prev => ({
        ...prev,
        [currentlyFetchingPackage]: inventories
      }));

      // Clear the currently fetching package
      setCurrentlyFetchingPackage(null);
    }
  }, [packageInventoriesData, currentlyFetchingPackage]);



  // // Apollo GraphQL query for real data
  // const { data: apolloData, loading: apolloLoading, error: apolloError, refetch } = useApolloQuery(DELIVERY_REPORTS_QUERY, {
  // variables: {
  //   accessToken: token,
  //   campaignId: campaignId,
  //   page: currentPage - 1,
  //   size: itemsPerPage,
  //   sort: "lastModifiedDate,desc",
  //   search: searchTerm,
  // },
  //   skip: !token || !campaignId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
  //   notifyOnNetworkStatusChange: true,
  //   errorPolicy: 'all',
  //   fetchPolicy: "no-cache",
  // });

  // Fallback to mock data for development (only when explicitly enabled)
  // const { data: mockDeliveryData, isLoading: mockLoading, error: mockError } = useQuery({
  //   queryKey: ['/api/delivery-report', campaignId, currentPage, searchTerm],
  //   queryFn: async () => {
  //     // Only use mock data when explicitly enabled
  //     if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
  //       const mockResponse = await generateMockData(DELIVERY_REPORTS_QUERY);
  //       return mockResponse.data.deliveryReports || [];
  //     }

  //     // Return null if mock data is not enabled
  //     return null;
  //   },
  //   enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true' && !!campaignId
  // });

  // Data processing effects
  // useEffect(() => {
  //   const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
  //   setIsLoading(apolloLoading || includeMockLoading);
  //   setError(apolloError ? apolloError.message : (mockError ? (mockError as Error).message : null));
  // }, [apolloLoading, mockLoading, apolloError, mockError]);

  // Helper function to calculate duration
  const calculateDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1; // add 1 for inclusive
    return duration > 0 ? `${duration} Day${duration > 1 ? 's' : ''}` : '0 Days';
  };

  // // Process delivery report data when Apollo data changes or mock data is available
  useEffect(() => {
    // if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
    //   // Use mock data
    //   if (mockDeliveryData && Array.isArray(mockDeliveryData)) {
    //     const reports: Report[] = mockDeliveryData;

    //     // Map mock data to DeliveryLogItem format
    //     const mappedLogs: DeliveryReportItem[] = reports.map(report => ({
    //       id: report.id,
    //       campaignId: report.campaignId,
    //       billboardId: report.inventoryId,
    //       billboardName: report.inventoryName,
    //       billboardImage: report.inventoryThumbnailUrl || "https://picsum.photos/400/300?random=1",
    //       date: new Date(report.startDate.date).toLocaleDateString(),
    //       startTime: "06:00",
    //       endTime: "22:00",
    //       loopDuration: 60,
    //       slotDuration: 10,
    //       playCount: 144,
    //       impressions: 8640,
    //       creativeName: "Campaign_Creative.mp4",
    //       creativeId: report.inventoryReferenceId || report.inventoryId,
    //       loopShare: 16.7,
    //       billboardType: report.inventoryDetails.type || "Digital",
    //       startDate: new Date(report.startDate.date).toLocaleDateString('en-GB', {
    //         day: '2-digit',
    //         month: 'short',
    //         year: 'numeric'
    //       }),
    //       endDate: new Date(report.endDate.date).toLocaleDateString('en-GB', {
    //         day: '2-digit',
    //         month: 'short',
    //         year: 'numeric'
    //       }),
    //       contentType: report.inventoryDetails.type || "Digital",
    //       status: report.campaignStatus.toLowerCase() as 'active' | 'completed' | 'pending',
    //     }));

    //     // Set campaign details from the first report
    //     if (reports.length > 0) {
    //       setCampaign({
    //         id: reports[0].campaignId,
    //         name: reports[0].campaignName,
    //         dealId: reports[0].dealId,
    //         duration: calculateDuration(reports[0].startDate.date, reports[0].endDate.date),
    //         startDate: reports[0].startDate.date,
    //         endDate: reports[0].endDate.date,
    //         status: reports[0].campaignStatus,
    //       });

    //       // Initialize date picker inputs
    //       setStartDate(new Date(reports[0].startDate.date).toISOString().split('T')[0]);
    //       setEndDate(new Date(reports[0].endDate.date).toISOString().split('T')[0]);
    //     }

    //     setDeliveryLogs(mappedLogs);
    //     setFilteredLogs(mappedLogs);
    //     setTotalPages(Math.ceil(reports.length / itemsPerPage));
    //   }
    // } else {
    // Use real GraphQL data
    if (deliveryreports && Array.isArray(deliveryreports)) {
      const reports: Report[] = deliveryreports;
      console.log("Delivery Reports:", reports);

      // Map API data to DeliveryLogItem format
      const mappedLogs: DeliveryReportItem[] = reports.map(report => ({
        id: report.id,
        campaignId: report?.campaignId,
        billboardId: report?.inventoryId,
        billboardName: report.inventoryName,
        billboardImage: report.inventoryThumbnailUrl || "https://picsum.photos/400/300?random=1",
        date: new Date(report.startDate.date).toLocaleDateString(),
        startTime: "06:00",
        endTime: "22:00",
        loopDuration: 60,
        slotDuration: 10,
        playCount: 144,
        impressions: 8640,
        creativeName: "Campaign_Creative.mp4",
        creativeId: report.inventoryReferenceId || report.inventoryId,
        loopShare: 16.7,
        billboardType: report?.inventoryType || "Digital",
        startDate: new Date(report.startDate.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        endDate: new Date(report.endDate.date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        contentType: report.inventoryDetails?.type || "Digital",
        status: report.campaignStatus.toLowerCase() as 'active' | 'completed' | 'pending',
        mediaOwnerId: report.inventoryDetails?.mediaOwnerId,
        mediaOwnerName: report.inventoryDetails?.mediaOwnerName,
        isSpotBased: report.isSpotBased,
      }));

      // Set campaign details from the first report
      if (reports.length > 0) {
        setCampaign({
          id: reports[0].campaignId,
          name: reports[0].campaignName,
          dealId: reports[0].dealId,
          duration: calculateDuration(reports[0].startDate.date, reports[0].endDate.date),
          startDate: reports[0].startDate.date,
          endDate: reports[0].endDate.date,
          status: reports[0].campaignStatus,
        });

        // Initialize date picker inputs
        setStartDate(new Date(reports[0].startDate.date).toISOString().split('T')[0]);
        setEndDate(new Date(reports[0].endDate.date).toISOString().split('T')[0]);
      }

      setDeliveryLogs(mappedLogs);
      // setFilteredLogs(mappedLogs);
      setTotalPages(reports[0]?.totalPages || Math.ceil(reports.length / itemsPerPage));
    }
    // }
  }, [deliveryreports, itemsPerPage]);

  // Mock campaign data - fallback for when no API data is available
  const mockCampaign: CampaignInfo = {
    id: "1",
    name: "OnePlus Nord 5G Campaign",
    dealId: "E-2023-061200000007",
    duration: "29 Days",
    startDate: "2023-07-01",
    endDate: "2023-07-31",
    status: "live"
  };

  // Apply filters when they change
  useEffect(() => {
    let filtered = deliveryLogs;

    // Filter by billboard if not "All"
    if (selectedBillboard !== "all") {
      filtered = filtered.filter(log => log.billboardName === selectedBillboard);
    }

    // Filter by date range
    if (startDate && endDate) {
      filtered = filtered.filter(log => {
        const logStartDate = new Date(log.startDate).setHours(0, 0, 0, 0);
        const logEndDate = new Date(log.endDate).setHours(0, 0, 0, 0);
        const filterStart = new Date(startDate).setHours(0, 0, 0, 0);
        const filterEnd = new Date(endDate).setHours(0, 0, 0, 0);

        // Check for overlap
        return logStartDate <= filterEnd && logEndDate >= filterStart;
      });
    }

    setFilteredLogs(filtered);
  }, [selectedBillboard, startDate, endDate, deliveryLogs]);

  // Reset page only when filters change (not when deliveryLogs change)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBillboard, startDate, endDate]);

  // Calculate summary stats
  const totalPlays = filteredLogs.reduce((sum, item) => sum + (item.playCount || 0), 0);
  const totalImpressions = filteredLogs.reduce((sum, item) => sum + (item.impressions || 0), 0);
  const averageLoopShare = filteredLogs.length > 0 ?
    filteredLogs.reduce((sum, item) => sum + (item.loopShare || 0), 0) / filteredLogs.length : 0;

  // Pagination logic for current items (only for mock data, API handles pagination)
  const [paginatedLogs, setPaginatedLogs] = useState<DeliveryReportItem[]>([]);
  useEffect(() => {
    setPaginatedLogs(import.meta.env.VITE_APP_MOCK_DATA === 'true'
      ? filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      : filteredLogs); // API already returns paginated data
  }, [filteredLogs]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Let useEffect handle the data fetching
  };

  const handleViewDetail = (item: DeliveryReportItem) => {
    // Get the dealId from the campaign data
    const dealId = campaign?.dealId || mockCampaign.dealId;
    console.log('Navigating to proof-of-logs with dealId:', dealId); // Debug log
    navigate(`${base_path}/proof-of-logs?campaign=${campaignId}&billboard=${item.billboardId}&dealId=${dealId}&billboardName=${encodeURIComponent(item.billboardName)}`);
  };

  const handleDownloadReport = (format: 'pdf' | 'csv') => {
    // In real app, this would generate and download the report
    console.log(`Downloading ${format.toUpperCase()} report`);
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return "N/A";
    return new Date(`2023-07-01T${timeString}:00`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const [billboards, setBillboards] = useState<string[]>([]);
  useEffect(() => {
    // Get unique billboard names for filter dropdown
    setBillboards(Array.from(new Set(deliveryLogs.map(log => log.billboardName))));
  }, [deliveryLogs]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">

        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Delivery Report...</h1>
            <p className="text-gray-600 mt-2">Please wait while we fetch your delivery data.</p>
          </div>
        </div>

      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">

        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`${base_path}/campaigns`)}
              className="hover:bg-emerald-50"
              size="sm"
            >
              <ChevronLeft className="h-3 w-3 mr-1" />
              Back
            </Button>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Error Loading Delivery Report</h1>
            <p className="text-gray-600 mt-2">{error}</p>
            <div className="mt-4 space-x-2">
              <Button
                variant="outline"
                onClick={() => navigate(`${base_path}/campaigns`)}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Campaigns
              </Button>
              <Button
                onClick={async () => {
                  await fetchData();
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>

      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 py-6">
        {/* Back Button at Top */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-emerald-600">Delivery Report</h1>
          <Button
            variant="outline"
            onClick={() => navigate("/my-campaigns")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">Get detailed reports on campaign performance and media delivery</p>
          {/* <div className="flex items-center space-x-2">
            <Button
              onClick={() => handleDownloadReport('pdf')}
              variant="outline"
              size="sm"
              className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
            >
              <FileText className="h-4 w-4 mr-2 text-emerald-600" />
              PDF Report
            </Button>
            <Button
              onClick={() => handleDownloadReport('csv')}
              variant="outline"
              size="sm"
              className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
              CSV Export
            </Button>
          </div> */}
        </div>

        {/* Campaign Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-600">Campaign Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Campaign name</span>
                <p className="font-medium">{campaign?.name || mockCampaign.name}</p>
              </div>
              <div>
                <span className="text-gray-600">Deal ID</span>
                <p className="font-medium">{campaign?.dealId || mockCampaign.dealId}</p>
              </div>
              <div>
                <span className="text-gray-600">Duration</span>
                <p className="font-medium">{campaign?.duration || mockCampaign.duration}</p>
              </div>
              <div>
                <span className="text-gray-600 mx-2">Status</span>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  {campaign?.status || mockCampaign.status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Play className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Plays</p>
                  <p className="text-2xl font-bold text-gray-900">{totalPlays.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Impressions</p>
                  <p className="text-2xl font-bold text-gray-900">{totalImpressions.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg Loop Share</p>
                  <p className="text-2xl font-bold text-gray-900">{averageLoopShare.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <MapPin className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Screens</p>
                  <p className="text-2xl font-bold text-gray-900">{deliveryLogs.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-600">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* <div>
                <Label htmlFor="date-range" className="text-sm font-medium text-gray-700">
                  Date Range
                </Label>
                <Select value={selectedDateRange} onValueChange={setSelectedDateRange}>
                  <SelectTrigger id="date-range"
                    className="bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm text-sm">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Dates</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              <div>
                <Label htmlFor="billboard-filter" className="text-sm font-medium text-gray-700">
                  Inventory
                </Label>
                <Select value={selectedBillboard} onValueChange={setSelectedBillboard}>
                  <SelectTrigger id="billboard-filter"
                    className="bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm text-sm">
                    <SelectValue placeholder="All Billboards" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Inventories</SelectItem>
                    {billboards.map((billboard, index) => (
                      <SelectItem key={index} value={billboard}>{billboard}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start-date" className="text-sm font-medium text-gray-700">
                  Start Date
                </Label>
                <div className="relative">
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="focus:border-emerald-500 focus:ring-emerald-500 [&::-webkit-calendar-picker-indicator]:text-emerald-600 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:transform [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 pr-8"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="end-date" className="text-sm font-medium text-gray-700">
                  End Date
                </Label>
                <div className="relative">
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="focus:border-emerald-500 focus:ring-emerald-500 [&::-webkit-calendar-picker-indicator]:text-emerald-600 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:transform [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 pr-8"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Report Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-emerald-600">Delivery Report</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Inventories</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Purchase Type</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Start Date</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">End Date</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Inventory Type</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.length > 0 ? (
                    paginatedLogs.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div className="flex items-center space-x-3">
                              <img
                                src={item.billboardImage}
                                alt={item.billboardName}
                                className="w-12 h-8 object-cover rounded"
                              />
                              <div>
                                <p className="font-medium text-gray-900" title={item.billboardName}>
                                  {item.billboardName.length > 30
                                    ? `${item.billboardName.substring(0, 30)}...` 
                                    : item.billboardName}
                                </p>
                                <p className="text-sm text-emerald-600 font-medium">
                                  By {item.billboardType.toLowerCase().includes('package') ? 'Multiple Media Owners' : item.mediaOwnerName}
                                </p>
                                <p className="text-sm text-gray-500">{item.creativeId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-gray-900">{getPurchaseType(item.billboardType, item.contentType, item.isSpotBased)}</td>
                          <td className="p-3 text-sm text-gray-900">{item.startDate}</td>
                          <td className="p-3 text-sm text-gray-900">{item.endDate}</td>
                          <td className="p-3">
                            <Badge className={`text-xs px-2 py-1 ${item.billboardType.toLowerCase().includes('digital') ?
                              'bg-blue-100 text-blue-700 border-blue-200' :
                              (item.billboardType.toLowerCase().includes('classic') || item.billboardType.toLowerCase().includes('static')) ?
                                'bg-green-100 text-green-700 border-green-200' :
                                item.billboardType.toLowerCase().includes('package') ?
                                  'bg-purple-100 text-purple-700 border-purple-200' :
                                  'bg-gray-100 text-gray-700 border-gray-200'
                              }`}>
                              <div className={`w-2 h-2 rounded-full mr-1 ${item.billboardType.toLowerCase().includes('digital') ? 'bg-blue-500' :
                                (item.billboardType.toLowerCase().includes('classic') || item.billboardType.toLowerCase().includes('static')) ?
                                  'bg-green-500' :
                                  item.billboardType.toLowerCase().includes('package') ?
                                    'bg-purple-500' :
                                    'bg-gray-500'
                                }`}></div>
                              <span className="capitalize">{_inventoryTypeDisplayMapping[item.billboardType.toLowerCase()] || item.billboardType.toLowerCase()}</span>
                            </Badge>
                          </td>
                          <td className="p-3">
                            {item.billboardType.toLowerCase().includes('classic') || item.billboardType.toLowerCase().includes('static') ? (
                              // No button for classic billboards
                              null
                            ) : item.billboardType.toLowerCase().includes('package') ? (
                              // Expand button for package billboards
                              <Button
                                onClick={() => togglePackageExpansion(item.id)}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                {expandedPackages.includes(item.id) ? 'Collapse' : 'Expand'}
                                <ChevronDown
                                  className={`h-4 w-4 mr-1 transition-transform duration-200 ${expandedPackages.includes(item.id) ? 'rotate-180' : ''
                                    }`}
                                />
                              </Button>
                            ) : (
                              // View button for digital billboards
                              <Button
                                onClick={() => handleViewDetail(item)}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            )}
                          </td>
                        </tr>

                        {/* Package Individual Billboards */}
                        {item.billboardType.toLowerCase() === 'package' &&
                          expandedPackages.includes(item.id) && (
                            <>
                              {/* Show loading state if data is being fetched */}
                              {packageInventoriesLoading && currentlyFetchingPackage === item.id ? (
                                <tr className="bg-purple-50/50">
                                  <td colSpan={6} className="text-center py-4">
                                    <div className="flex items-center justify-center gap-2">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                      <span className="text-sm text-gray-600">Loading package inventories...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : packageInventories[item.id]?.length > 0 ? (
                                (packageInventories[item.id] || []).map((packageInventory: any) => (
                                  <tr
                                    key={`${item.id}-${packageInventory.inventoryId}`}
                                    className="bg-purple-50/50 hover:bg-purple-100/50"
                                    title="Package Inventory"
                                  >
                                    <td className="p-3 pl-12 relative">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 border-l-2 border-purple-300 h-full absolute left-6"></div>
                                        <img
                                          src={packageInventory.imageUrl || item.billboardImage}
                                          alt={packageInventory.name}
                                          className="w-12 h-8 object-cover rounded"
                                        />
                                        <div>
                                          <p className="font-medium text-gray-900">{packageInventory.name}</p>
                                          <p className="text-sm text-emerald-600 font-medium">
                                            By {packageInventory.publisher.name}
                                          </p>
                                          <p className="text-sm text-gray-500">{packageInventory.referenceId}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="p-3 text-sm text-gray-900">-</td>
                                    <td className="p-3 text-sm text-gray-900">{item.startDate}</td>
                                    <td className="p-3 text-sm text-gray-900">{item.endDate}</td>
                                    <td className="p-3">
                                      <Badge className="text-xs px-2 py-1 bg-blue-100 text-blue-700 border-blue-200">
                                        <div className="w-2 h-2 rounded-full mr-1 bg-blue-500"></div>
                                        {packageInventory.type || 'Digital'}
                                      </Badge>
                                    </td>
                                    <td className="p-3">
                                      <Button
                                        onClick={() => handleViewDetail({
                                          ...item,
                                          billboardId: packageInventory.inventoryId,
                                          billboardName: packageInventory.name
                                        })}
                                        size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      >
                                        <Eye className="h-4 w-4 mr-1" />
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr className="bg-purple-50/50">
                                  <td colSpan={6} className="text-center py-4 text-sm text-gray-600">
                                    No package inventories found
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        No delivery logs found. Adjust your filters or check back later.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredLogs.length)} of {filteredLogs.length} results
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </Button>

                  <div className="flex items-center space-x-1">
                    {(() => {
                      if (totalPages <= 5) {
                        return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        ));
                      }

                      const pages = [];

                      // Always show first page
                      pages.push(
                        <Button key={1} variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => handlePageChange(1)} className="w-8 h-8 p-0">1</Button>
                      );

                      // Show dots if needed
                      if (currentPage > 4) pages.push(<span key="dots1" className="px-2 text-gray-500">...</span>);

                      // Show pages around current
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);
                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(
                            <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => handlePageChange(i)} className="w-8 h-8 p-0">{i}</Button>
                          );
                        }
                      }

                      // Show dots if needed
                      if (currentPage < totalPages - 3) pages.push(<span key="dots2" className="px-2 text-gray-500">...</span>);

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(
                          <Button key={totalPages} variant={currentPage === totalPages ? "default" : "outline"} size="sm" onClick={() => handlePageChange(totalPages)} className="w-8 h-8 p-0">{totalPages}</Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronLeft className="h-4 w-4 rotate-180" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


    </div>
  );
}